import { useMemo } from "react";
import {
  Sparkles,
  Flame,
  Layers,
  TrendingDown,
  CalendarClock,
  ArrowRight,
} from "lucide-react";

// ---------- Pure derivation ----------
// Reads the studio payload and produces a small ranked list of actionable
// nudges. All math is window-relative — no backend extension required.

const SUB_DAY_STACK_THRESHOLD = 3;     // 3+ short tasks on same day → suggest batching
const STREAK_NEAR_CAP_RATIO = 0.9;     // ≥ 90 % capacity counts as "hot"
const STREAK_MIN_DAYS = 3;             // 3+ consecutive hot days → burnout risk
const LIGHT_LOAD_RATIO = 0.25;         // person with < 25 % of team avg load
const LIGHT_LOAD_AVG_FLOOR = 8;        // only surface if team has real work (>8h avg)

const isoToDate = (iso) => new Date(iso + "T00:00:00Z");
const daysApart = (a, b) => Math.round((isoToDate(b) - isoToDate(a)) / 86400000);
const fmtHrs = (h) => {
  const v = Math.round(h * 10) / 10;
  return Number.isInteger(v) ? `${v}h` : `${v.toFixed(1)}h`;
};

function deriveInsights({ people, tasks, allocations, conflicts, capacity }) {
  const out = [];
  const peopleById = Object.fromEntries(
    (people || []).map((p) => [p.user_id, p])
  );
  const nameOf = (uid) => {
    const p = peopleById[uid];
    return p?.name || p?.email || "Someone";
  };

  // 1) Stacked sub-day tasks — (user, day) where 3+ tasks have duration < 1
  const stackMap = new Map(); // key: uid|iso → count
  for (const t of tasks || []) {
    const d = Number(t.duration) || 1;
    if (!t.assigned_user_id || !t.start) continue;
    if (d > 0 && d < 1) {
      const key = `${t.assigned_user_id}|${t.start}`;
      stackMap.set(key, (stackMap.get(key) || 0) + 1);
    }
  }
  for (const [key, n] of stackMap) {
    if (n < SUB_DAY_STACK_THRESHOLD) continue;
    const [uid, iso] = key.split("|");
    if (!peopleById[uid]) continue;
    out.push({
      id: `stack-${uid}-${iso}`,
      kind: "stack",
      severity: "info",
      user_id: uid,
      iso_date: iso,
      icon: Layers,
      message: `${nameOf(uid)} has ${n} short tasks stacked on ${iso}`,
      hint: "Consider batching or moving to a less loaded day.",
    });
  }

  // 2) Worst over-capacity day (conflicts already sorted by total_hours desc)
  const worst = (conflicts || [])[0];
  if (worst && peopleById[worst.user_id]) {
    const over = worst.total_hours - worst.capacity;
    out.push({
      id: `over-${worst.user_id}-${worst.iso_date}`,
      kind: "overload",
      severity: "warn",
      user_id: worst.user_id,
      iso_date: worst.iso_date,
      icon: Flame,
      message: `${nameOf(worst.user_id)} is at ${fmtHrs(worst.total_hours)} on ${worst.iso_date}`,
      hint: `${fmtHrs(over)} over the ${worst.capacity}h cap.`,
    });
  }

  // 3) Sustained near-capacity streak — burnout risk
  const cap = Number(capacity) || 8;
  for (const [uid, days] of Object.entries(allocations || {})) {
    if (!peopleById[uid]) continue;
    const sortedDays = Object.entries(days).sort((a, b) =>
      a[0] < b[0] ? -1 : 1
    );
    let run = [];
    let best = [];
    for (const [iso, hrs] of sortedDays) {
      if (hrs >= cap * STREAK_NEAR_CAP_RATIO) {
        if (run.length === 0) {
          run = [iso];
        } else {
          // allow weekend gap (≤3 days) so Fri→Mon still counts as a streak
          const gap = daysApart(run[run.length - 1], iso);
          if (gap > 0 && gap <= 3) run.push(iso);
          else {
            if (run.length > best.length) best = run;
            run = [iso];
          }
        }
      } else {
        if (run.length > best.length) best = run;
        run = [];
      }
    }
    if (run.length > best.length) best = run;
    if (best.length >= STREAK_MIN_DAYS) {
      out.push({
        id: `streak-${uid}-${best[0]}`,
        kind: "streak",
        severity: "warn",
        user_id: uid,
        iso_date: best[0],
        icon: CalendarClock,
        message: `${nameOf(uid)} is near-capacity ${best.length} days running`,
        hint: `${best[0]} → ${best[best.length - 1]} · consider leveling.`,
      });
    }
  }

  // 4) Light load — person with much less work than team average
  const totalsByPerson = new Map();
  for (const [uid, days] of Object.entries(allocations || {})) {
    let total = 0;
    for (const hrs of Object.values(days)) total += hrs;
    totalsByPerson.set(uid, total);
  }
  if (totalsByPerson.size >= 3) {
    const sums = [...totalsByPerson.values()];
    const avg = sums.reduce((a, b) => a + b, 0) / sums.length;
    if (avg > LIGHT_LOAD_AVG_FLOOR) {
      let lightest = null;
      for (const [uid, total] of totalsByPerson) {
        if (lightest == null || total < lightest.total) lightest = { uid, total };
      }
      if (
        lightest &&
        peopleById[lightest.uid] &&
        lightest.total < avg * LIGHT_LOAD_RATIO
      ) {
        out.push({
          id: `light-${lightest.uid}`,
          kind: "light",
          severity: "info",
          user_id: lightest.uid,
          iso_date: null,
          icon: TrendingDown,
          message: `${nameOf(lightest.uid)} has only ${fmtHrs(lightest.total)} this window`,
          hint: `Team avg is ${fmtHrs(avg)} — room to absorb work.`,
        });
      }
    }
  }

  // Rank: warns first, then info; cap at 5
  out.sort((a, b) => {
    if (a.severity === b.severity) return 0;
    return a.severity === "warn" ? -1 : 1;
  });
  return out.slice(0, 5);
}

// ---------- Component ----------

export default function StudioInsights({
  people,
  tasks,
  allocations,
  conflicts,
  capacity,
  onInsightClick,
}) {
  const insights = useMemo(
    () => deriveInsights({ people, tasks, allocations, conflicts, capacity }),
    [people, tasks, allocations, conflicts, capacity]
  );

  if (insights.length === 0) {
    return (
      <div
        className="rounded-md border border-zinc-800 bg-zinc-950/60 p-4"
        data-testid="studio-insights-empty"
      >
        <div className="flex items-center gap-2 mb-1.5">
          <Sparkles className="w-3.5 h-3.5 text-amber-500/70" />
          <div className="text-overline text-zinc-500">Studio Insights</div>
        </div>
        <p className="text-xs text-zinc-500">
          Nothing flagged in this window. Your roster is balanced.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-zinc-800 bg-zinc-950/60"
      data-testid="studio-insights"
    >
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
        <span className="text-overline text-amber-500/90">
          Studio Insights · {insights.length}
        </span>
      </div>
      <ul className="divide-y divide-zinc-800/60">
        {insights.map((ins) => {
          const Icon = ins.icon;
          const isWarn = ins.severity === "warn";
          return (
            <li key={ins.id}>
              <button
                type="button"
                data-testid={`studio-insight-${ins.kind}-${ins.user_id}`}
                onClick={() => onInsightClick?.(ins)}
                className="w-full text-left px-4 py-3 hover:bg-zinc-900/50 transition-colors group"
              >
                <div className="flex items-start gap-2.5">
                  <span
                    className={`mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-sm shrink-0 ${
                      isWarn
                        ? "bg-orange-500/10 text-orange-400"
                        : "bg-amber-500/10 text-amber-400"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-zinc-200 leading-snug">
                      {ins.message}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
                      {ins.hint}
                    </div>
                  </div>
                  <ArrowRight className="w-3 h-3 text-zinc-600 group-hover:text-amber-500 transition-colors mt-1 shrink-0" />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Exported for unit testing
export { deriveInsights };
